import React, { useState, useEffect } from 'react';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { documentsAPI } from '../../lib/api';
import { FileText, Download, Scroll, FilePdf, FileDoc } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const documentTypes = [
  { key: 'constitution', label: 'Constitutions' },
  { key: 'decree', label: 'Decrees' },
  { key: 'letter_patent', label: 'Letters Patent' },
  { key: 'report', label: 'Reports' },
];

const DocumentsPage = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const response = await documentsAPI.getPublic();
        setDocuments(response.data);
      } catch (error) {
        console.log('Error fetching documents');
      } finally {
        setLoading(false);
      }
    };
    fetchDocuments();
  }, []);

  const filteredDocuments = filter === 'all' 
    ? documents 
    : documents.filter(d => d.document_type === filter);

  const getDocIcon = (contentType) => {
    if (contentType?.includes('pdf')) return FilePdf;
    if (contentType?.includes('word') || contentType?.includes('document')) return FileDoc;
    return FileText;
  };

  const handleDownload = async (doc) => {
    try {
      const response = await documentsAPI.download(doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename || `${doc.title}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  return (
    <PublicLayout>
      {/* Header */}
      <section 
        className="py-20 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_chivalric-office/artifacts/jp18xs29_81cf8eff-c43b-4ea8-9957-f7f9d052a6db.jpeg)'
        }}
        data-testid="documents-header"
      >
        <div className="absolute inset-0 bg-[#1A1614]/70" />
        <div className="relative z-10 text-center">
          <p className="text-overline mb-4">Official Records</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-white mb-4">
            Documents
          </h1>
          <p className="text-[#F5F2EB]/80 max-w-2xl mx-auto px-4">
            Public constitutions, decrees, and official documents of the Grand Priory
          </p>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 bg-[#F5F2EB] border-b border-[#D8D2C4]">
        <div className="max-w-4xl mx-auto px-4 flex flex-wrap gap-4 justify-center">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              filter === 'all' 
                ? 'bg-[#7B1818] text-white' 
                : 'text-[#4A453E] hover:text-[#7B1818]'
            }`}
            data-testid="filter-all"
          >
            All Documents
          </button>
          {documentTypes.map(type => (
            <button
              key={type.key}
              onClick={() => setFilter(type.key)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                filter === type.key 
                  ? 'bg-[#7B1818] text-white' 
                  : 'text-[#4A453E] hover:text-[#7B1818]'
              }`}
              data-testid={`filter-${type.key}`}
            >
              {type.label}
            </button>
          ))}
        </div>
      </section>

      {/* Documents List */}
      <section className="py-16 bg-white" data-testid="documents-list">
        <div className="max-w-4xl mx-auto px-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : filteredDocuments.length > 0 ? (
            <div className="space-y-4">
              {filteredDocuments.map(doc => {
                const Icon = getDocIcon(doc.content_type);
                return (
                  <div 
                    key={doc.id}
                    className="bg-[#F5F2EB] border border-[#D8D2C4] p-6 flex items-center justify-between"
                    data-testid={`document-item-${doc.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <Icon size={32} className="text-[#7B1818]" />
                      <div>
                        <p className="text-overline text-xs mb-1">
                          {doc.document_type?.replace('_', ' ').toUpperCase() || 'DOCUMENT'}
                        </p>
                        <h3 className="font-heading text-lg text-[#1A1614]">{doc.title}</h3>
                        {doc.description && (
                          <p className="text-sm text-[#4A453E] mt-1">{doc.description}</p>
                        )}
                      </div>
                    </div>
                    {doc.file_path && (
                      <button
                        onClick={() => handleDownload(doc)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-[#7B1818] hover:bg-[#7B1818] hover:text-white border border-[#7B1818] transition-colors"
                        data-testid={`download-${doc.id}`}
                      >
                        <Download size={16} />
                        Download
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <Scroll size={48} className="mx-auto text-[#8A847A] mb-4" />
              <p className="text-[#8A847A]">No public documents available at this time.</p>
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
};

export default DocumentsPage;
